import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { habitsAPI } from '../services/api';
import type { HabitCreate, HabitUpdate } from '../types';
import { useErrorHandler } from './useErrorHandler';

export const useHabits = (activeOnly = true) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const { data: habits = [], isLoading, error } = useQuery({
    queryKey: ['habits', { activeOnly }],
    queryFn: () => habitsAPI.getAll({ active_only: activeOnly }),
    retry: 1,
    onError: handleError,
  });

  const createMutation = useMutation({
    mutationFn: (habit: HabitCreate) => habitsAPI.create(habit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['habits'] });
    },
    onError: handleError,
  });

  const updateMutation = useMutation({
    mutationFn: ({ habitId, update }: { habitId: string; update: HabitUpdate }) =>
      habitsAPI.update(habitId, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['habits'] });
      queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
    onError: handleError,
  });

  const deleteMutation = useMutation({
    mutationFn: (habitId: string) => habitsAPI.delete(habitId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['habits'] });
      queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
    onError: handleError,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ habitId, date, opts }: { habitId: string; date: string; opts?: { duration_minutes?: number; notes?: string } }) =>
      habitsAPI.toggle(habitId, date, opts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['habits'] });
    },
    onError: handleError,
  });

  const reorderMutation = useMutation({
    mutationFn: (habitIds: string[]) => habitsAPI.reorder(habitIds),
    onError: handleError,
  });

  return {
    habits,
    isLoading,
    error: error as Error | null,
    createHabit: createMutation.mutateAsync,
    updateHabit: updateMutation.mutateAsync,
    deleteHabit: deleteMutation.mutateAsync,
    toggleCompletion: toggleMutation.mutateAsync,
    reorderHabits: reorderMutation.mutateAsync,
    isToggling: toggleMutation.isLoading,
  };
};
